import React from 'react';
import { ButtonProps, ButtonVariant, IconSize } from '../../../types/DesignSystem/Button';
import { BadgeVariant } from '../../../types/DesignSystem/Badge';
import Badge from '../Badge/Badge';
import Spinner from '../Spinner/Spinner';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';
import { BUTTON_INSET, CONTROL_GAP, CONTROL_HEIGHT, resolveControlSize } from '../controlSize';
import { useControlSize } from '../DensityProvider/DensityProvider';

/**
 * The filled variants rest on `accent-bold` and hover to `accent-bold-hover` — LIGHTER,
 * because the navy fill has no darker rung (§ 255); the outlined and ghost variants hover
 * to the accent tint. `DropdownButton` reads these same tables (§ 350). See
 * `okf/design-system/e2e.md` § 29. (§ 383 — this named the retired `blue-500` hover.)
 *
 * The `{ default, hover }` shape this used to have is gone. Only `.default` was ever
 * read (line 62 was its single call site), no prop exposed `.hover`, and nothing
 * imported the record — so all three `hover:` strings were dead, and one of them
 * was three months out of step with the `hover:` variants inside `.default`. The
 * live hover state is, and always was, the `hover:*` classes below.
 */
const variantStyles: Record<ButtonVariant, string> = {
  /* `shadow-ds-topline` (§ 221, promoted from the § 219 showcase candidate): the
     1px inset top-light both FILLED variants wear — the machined read a flat
     fill lacks. Enabled fills only, by construction: disabledStyles is a
     separate map, so the sheen falls away with the fill when disabled/loading. */
  /* `tw-border tw-border-transparent` (§ 246): the filled variants carry a border
     they never show, so the box is the SAME size enabled and disabled — the disabled
     shape below is outlined, and a button that grew 2px on disable would shift the
     toolbar beside it. */
  /* § 253 — the filled action also paints `--ds-cta-fill` (a flat one-colour gradient in
     light, Volt's azure gradient in dark) and wears `--ds-glow`, its material in both themes
     since § 261 (a top-light and a drop; it was transparent in light before that).
     The bg-colour classes stay: they are the fallback where tokens.css never loaded. */
  primary: 'tw-bg-accent-bold tw-border tw-border-transparent tw-text-ink-inverse hover:tw-bg-accent-bold-hover tw-bg-ds-cta hover:tw-bg-ds-cta-hover tw-shadow-ds-glow',
  // Resting border is `grey-200`, not `blue-900`. A dialog footer pairs this with a
  // `blue-900`-filled primary, and a `blue-900` OUTLINE is the same 8.53:1 ink — two
  // blue objects of equal weight, so neither reads as the primary action. The label
  // stays `blue-900`, which is what marks it as an action at all; only the outline
  // steps back. Hover still goes fully blue (`blue-100` fill + `blue-500` border), so
  // the boundary strengthens 1.39:1 -> 5.62:1 rather than weakening.
  // Also aligns with `DropdownButton.secondary`, which moved to `grey-200` in § 31.
  secondary:
    'tw-bg-surface tw-text-accent tw-border tw-border-edge hover:tw-bg-accent-tint hover:tw-border-accent-hover',
  /* § 296 — TRANSPARENT at rest, not `surface`. A tertiary is the ghost beside a
     primary, and a ghost has no ground of its own. Painting `surface` made it a ghost
     only on `surface`: in a Dialog footer (sunken in light, recess in dark) and in the
     SidePanel header (raised) it rendered a visible box — measured live in dark, the
     footer's Cancel was a #141c2c slab +4.8 L* off its band, and the header's Cancel a
     slab −3.9 L* INTO the panel. IconButton.subtle has been transparent from birth;
     the two quiet buttons now agree. Ink and hover are untouched. */
  tertiary: 'tw-bg-transparent tw-text-accent hover:tw-bg-accent-tint',
  /* The quiet toolbar trigger (§ 65) — Sort / Filter / Bulk Action. Ink, not
     accent: a toolbar of three accent-blue words reads as three links, and the
     accent is reserved for the one primary action beside them. Transparent at
     rest so it sits on any surface; the hover is the neutral row ground. */
  subtle: 'tw-bg-transparent tw-text-ink hover:tw-bg-surface-hover-strong',
  /* The destructive outline action (§ 84) — Delete beside a primary Save, where
     a second FILL would compete for "the" action. Same grammar as secondary:
     the ink carries the meaning (`tone-danger-ink`), the resting border steps
     back to `edge`, and hover strengthens the boundary with `tone-danger-glyph`
     (§ 54's designated danger border) over the danger tint. A destructive
     CONFIRM is `danger-primary` below — NOT this one. */
  danger:
    'tw-bg-surface tw-text-tone-danger-ink tw-border tw-border-edge hover:tw-bg-tone-danger-tint hover:tw-border-tone-danger-glyph',
  /* The destructive CONFIRM (§ 167) — the Delete button inside a delete dialog,
     and nothing else.

     This used to be plain `primary`: a blue-filled "Delete" identical to the blue
     "Save" one dialog away. Everything else in that dialog said danger — the red
     header glyph, the word Delete, the copy — and then the button a user actually
     has to press was the same object as every safe confirm in the app. The colour
     is `tone-danger-bold`, the SAME #ad2a21 as the header glyph, so the dialog
     speaks one red.

     **Red is reserved for delete.** Stop, promote, discard-changes and every other
     confirm stay `primary`; if red marks any irreversible action it stops marking
     the one that destroys data. */
  'danger-primary':
    'tw-bg-tone-danger-bold tw-border tw-border-transparent tw-text-ink-inverse tw-shadow-ds-glow-danger hover:tw-bg-tone-danger-bold-hover',
};

/**
 * Disabled, **by the variant's shape** (§ 181).
 *
 * This used to be one string applied to all six variants:
 * `tw-bg-tone-neutral-tint tw-text-ink-tertiary`. An unconditional FILL — so a
 * disabled `tertiary` or `subtle` Button *grew a background it never had when
 * enabled*, and `tone-neutral-tint` is `#dadbdd`, the one tint in the whole tone
 * set that is not near-white:
 *
 * | tone | tint |     | tone | tint |
 * |---|---|---|---|---|
 * | info | `#eef4ff` | | danger | `#fce5e3` |
 * | success | `#e3fcf4` | | accent | `#e4d7ff` |
 * | warning | `#fcf6e3` | | **neutral** | **`#dadbdd`** |
 *
 * It is a BORDER colour (`grey-200`) doing duty as a surface. The result, measured
 * on Catalog Management's Data Review toolbar: the disabled "Update All" rendered as
 * a solid `#dadbdd` slab **100.7 x 36** and was the second-heaviest object on the
 * bar, after the blue primary — the one control you cannot press, drawn louder than
 * the two you can. Beside it sat a disabled `DropdownButton variant="tertiary"`,
 * which does this correctly (`disabled:tw-text-ink-disabled disabled:tw-bg-transparent`)
 * and rendered as quiet ghost text. Two components, the same variant name, opposite
 * treatment, ten pixels apart.
 *
 * So the fill now follows the shape the variant has when it is ENABLED:
 *
 * - filled (`primary`, `danger-primary`) keeps the neutral fill — **byte-identical to
 *   before**, and the same thing `DropdownButton.primary` does, so nothing that was
 *   already right moves;
 * - outlined (`secondary`, `danger`) keeps its outline, over `surface-sunken` rather
 *   than the white it has when enabled — the same thing `DropdownButton.secondary`
 *   already does (`disabled:tw-bg-surface-sunken`). Ink alone is a thin signal here,
 *   because `secondary`'s resting border is `edge` whether or not it is disabled, so
 *   without the tint an unavailable button differs from an available one only by the
 *   colour of its label;
 * - quiet (`tertiary`, `subtle`) keeps whatever it had: `tertiary` is white when
 *   enabled and stays white; `subtle` is transparent and stays transparent. They are
 *   indistinguishable on a white page and not on any other, which is the whole reason
 *   to keep them apart.
 *
 * No variant's box changes size: `secondary` and `danger` already carry `tw-border`
 * when enabled, and the ghost pair never had one.
 *
 * **The ink stays `ink-tertiary` and is deliberately NOT `ink-disabled`.** `ink-disabled`
 * is the system's chosen disabled ink — `Toggle`'s docblock argues for it by name — but
 * adopting it here would make every disabled button in the product fainter, which is the
 * opposite of the complaint that started this. Button is the outlier on ink; that is a
 * system-wide call, not one to make from a toolbar fix. (§ 383 — the hexes and ratios
 * quoted here predated the § 253 retheme; the tokens decide, and the tests measure them.)
 */
/*
 * § 246 (2026-09-09) supersedes the FILLED half of § 181. A disabled primary kept the
 * neutral-tint FILL — #dadbdd, the one tint in the set that is not near-white — so the
 * one control you cannot press stayed the heaviest object on the bar after the live
 * primary (§ 181 measured exactly this on the Data Review toolbar and fixed only the
 * ghosts). In dark the same fill sat on the hover ground and the button barely
 * existed (§ 241). Every disabled variant now collapses to ONE quiet shape — the
 * outlined one: sunken ground, hairline edge, tertiary ink. Unavailable controls
 * recede together; the live ones lead.
 */
const disabledStyles: Record<ButtonVariant, string> = {
  primary: 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-tertiary',
  'danger-primary': 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-tertiary',
  secondary: 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-tertiary',
  danger: 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-tertiary',
  /* § 296 — `tertiary` is transparent when enabled (see variantStyles) and stays so
     here: disabled follows the enabled shape (§ 181), and the shape is now "no
     ground" for both quiet variants. They differ by ink and by hover, not by a box. */
  tertiary: 'tw-bg-transparent tw-text-ink-tertiary',
  subtle: 'tw-bg-transparent tw-text-ink-tertiary',
};

/*
 * § 257 — a count is not a warning.
 *
 * `primary` returned `'warning'`, so the count on the primary action rendered in the
 * tone the system reserves for something being wrong: an amber pill on the navy fill
 * in light, and in dark the acid yellow § 253 restricted to warnings ONLY, on an
 * olive ground, measuring 4.24:1 against the azure button beneath it.
 * `danger-primary` fell through to `'secondary'` and put an accent-navy pill on a red
 * button. Both FILLED variants now take `Badge.primary`, which the Badge type has
 * documented from the start as "the bubble ON a filled button" — the surface colour
 * as the pill, the accent as the digits — so the count reads as punched out of the
 * fill rather than as a second, contradictory status.
 */
/**
 * § 350 — the variant's colours for a state, as ONE resolver every button-shaped
 * trigger shares. `DropdownButton` kept a private copy of the two tables above and the
 * copy drifted (a different secondary hover, different disabled inks), so a button
 * changed its look when it gained a menu. Disabled wins over loading; a loading
 * button keeps its RESTING look (§ 330) with the hovers taken off and the wait cursor.
 *
 * Exported from here rather than a new module so the class strings stay literal in
 * the file Tailwind scans for them.
 */
export const buttonVariantClass = (
  variant: ButtonVariant,
  { disabled = false, loading = false }: { disabled?: boolean; loading?: boolean } = {},
): string => {
  if (disabled) return `${disabledStyles[variant]} ${loading ? 'tw-cursor-wait' : 'tw-cursor-not-allowed'}`;
  if (loading) {
    const resting = variantStyles[variant].split(/\s+/).filter((c) => !c.startsWith('hover:')).join(' ');
    return `${resting} tw-cursor-wait`;
  }
  return variantStyles[variant];
};

const getBadgeVariant = (buttonVariant: ButtonVariant, disabled?: boolean): BadgeVariant => {
  if (disabled) return 'disabled';
  if (buttonVariant === 'primary' || buttonVariant === 'danger-primary') return 'primary';
  return 'secondary';
};

/* § 331 — the glyph box answers to the control scale's names too (`sm | md | lg`), so
   one call site can say `size="sm" iconSize="sm"`. The legacy words stay valid. */
const iconSizeClasses: Record<IconSize, string> = {
  small: 'tw-w-4 tw-h-4',
  medium: 'tw-w-5 tw-h-5',
  large: 'tw-w-6 tw-h-6',
  sm: 'tw-w-4 tw-h-4',
  md: 'tw-w-5 tw-h-5',
  lg: 'tw-w-6 tw-h-6',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  disabled = false,
  focusableWhenDisabled = false,
  loading = false,
  startIcon,
  endIcon,
  showIcon = true,
  showBadge = false,
  icon = null,
  iconSize = 'medium',
  iconPosition = 'start',
  badge,
  badgeContent,
  badgeVariant,
  children,
  className = '',
  size: sizeProp,
  type = 'button',
  'data-testid': dataTestId,
  ariaLabel,
  'aria-label': ariaLabelNative,
  onClick,
  'aria-disabled': ariaDisabledProp,
  ...rest
}, ref) {
  /*
    Height and inset come from `controlSize.ts`, not from this file. Button was the only
    control with a declared height (`tw-h-8`, 32px) while `TextField` computed 38 out of
    `py-2` + a line box + a border — so a Button next to a field was 6px shorter, and
    `DropdownButton` was 4px taller than this one. `md` is now 36 for all five.

    `tw-py-2` is gone: with a fixed height on a flex row, `items-center` does the vertical
    centring and the padding only fought it.
  */
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  /*
    § 391 — one slot per side, and a badge that shows when it has a value. The slots win; the
    older spellings resolve to exactly what they always drew (`buttonSlots.test` renders each
    pair and compares the markup), which is what lets the codemod fold them.
  */
  const start = startIcon ?? (showIcon && iconPosition === 'start' ? icon : null);
  const end = endIcon ?? (showIcon && iconPosition === 'end' ? icon : null);
  const badgeValue = badge ?? (showBadge ? badgeContent : undefined);
  /* § 335 — a disabled button that must keep focus (see `focusableWhenDisabled`): the
     same disabled shape, but no native attribute for the focus-fixup rule to act on. */
  const inertWhileDisabled = disabled && focusableWhenDisabled;
  const baseStyle =
    // `title` IS 14px/500, so `tw-font-medium` was a no-op (verified in a browser).
    `tw-flex tw-items-center tw-justify-center tw-rounded-ctl tw-text-title ${
      CONTROL_HEIGHT[size]} ${BUTTON_INSET[size]} ${CONTROL_GAP[size]} `
    // transition-all, not transition-colors: transform is what the press
    // animates, and transition-colors does not cover it. `ease-swift` (§ 219):
    // the browser's default curve makes a 150ms press-release read as lag.
    // `select-none` (§ 235): a double-click or an eager re-click flashed a text
    // selection across the label — native OS buttons never select; TableHeader's
    // sort button set the precedent.
    // § 330 — the press animation is withheld while busy, like the hover rules below.
    + `tw-select-none tw-transition-all tw-duration-fast tw-ease-swift${
      loading || inertWhileDisabled ? '' : ' motion-safe:active:tw-scale-[0.98]'}`;

  /*
    § 330 — a LOADING button is inert but stays FOCUSABLE. It used to be natively
    `disabled` (§ 209), and the HTML focus-fixup rule moves focus to <body> the moment a
    focused element becomes disabled — so a keyboard user who pressed Save lost their
    place in the form. Loading now renders `aria-disabled` (assistive tech still hears
    "unavailable") and cancels the click here, which also stops a `type="submit"` from
    submitting its form (implicit Enter-submission dispatches this same click). An
    explicit `disabled` still renders the native attribute, exactly as before.
  */
  const inertWhileLoading = loading && !disabled;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (inertWhileLoading || inertWhileDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  /*
    § 297 — a LOADING button keeps its fill. § 209 collapsed it to the disabled shape
    because the spinner's token ring could not read on the saturated fill; the effect was
    a primary that visibly lost its colour the instant you pressed it, which reads as the
    action being withdrawn rather than carried out. Spinner has an `inverse` tone now, so
    the fill stays, the label stays, the click is still blocked (by `handleClick` since
    § 330, `aria-disabled` + `aria-busy` below), and only the hover rules come off: a
    disabled element still matches `:hover`, and a button that is busy must not light up
    under the pointer as if it could take a second press. `disabled` outranks `loading`
    for the SHAPE, as it always has for the attribute. (§ 350 — the resolution lives in
    `buttonVariantClass`, shared with DropdownButton.)
  */
  const variantStyle = buttonVariantClass(variant, { disabled, loading });
  /* The spinner ON a fill is the inverse one; on an outlined or ghost button the token
     spinner reads as it always did. */
  const spinnerTone = !disabled && (variant === 'primary' || variant === 'danger-primary') ? 'inverse' : 'accent';

  // § 330/§ 332 — a caller's class replaces the conflicting component class instead of
  // leaving the winner to stylesheet order; the component's own classes are untouched.
  const finalClassName = withClassName(`${baseStyle} ${variantStyle} ${FOCUS_RING}`, className);

  return (
    <button
      /*
        `rest` FIRST, everywhere in this design system. Spread last, a caller's
        stray `onClick` would silently replace a control's own behaviour — the
        switch in `Toggle` would stop toggling — and the failure looks like a dead
        widget, not a prop conflict. So the rule is uniform: rest first, then
        everything the component must own. Where a deprecated prop has to yield to
        its native replacement, precedence is written out (see `aria-label` below)
        rather than left to spread order.
      */
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled && !focusableWhenDisabled}
      /* A caller's own `aria-disabled` (SubModelItem's focusable "Available with
         Configure" button) is kept; loading and § 335 only ever ADD it. */
      aria-disabled={inertWhileLoading || inertWhileDisabled || ariaDisabledProp}
      aria-busy={loading || undefined}
      onClick={handleClick}
      className={finalClassName}
      data-testid={dataTestId}
      aria-label={ariaLabelNative ?? ariaLabel}
    >
      {/* § 209: the spinner TAKES the icon slot — both icons are suppressed while
          loading so the box does not grow, and the label stays put. Spinner's own
          sr-only "Loading" joins the accessible name, which is the announcement.
          (§ 391: with BOTH slots filled, the spinner takes the start and the end
          slot empties — the box narrows by one glyph while it loads.) */}
      {loading && (
        <span className={`${iconSizeClasses[iconSize]} tw-flex tw-items-center tw-justify-center`}>
          <Spinner size="sm" tone={spinnerTone} data-testid={dataTestId ? `${dataTestId}-spinner` : undefined} />
        </span>
      )}
      {!loading && start && <span className={`${iconSizeClasses[iconSize]} tw-flex tw-items-center tw-justify-center`}>{start}</span>}
      {/*
        `min-w-0` so a width-constrained Button can actually clip its label.

        A flex item defaults to `min-width: auto`, which refuses to shrink below its
        content — so a `Button className="tw-max-w-[180px]"` with a long label rendered
        the BOX at 180px and let the text escape it on both sides, overlapping whatever
        sat beside it. Caught in Catalog Management's attribute-value grid, where a
        row of buttons became a pile of overlapping sentences.

        This is a no-op for every Button that has no width constraint — which is all of
        them today — because a flex item only shrinks when its container makes it. The
        clipping itself stays the caller's call: pass `<span className="tw-block
        tw-truncate">`. Forcing `truncate` here would silently stop any multi-line label
        from wrapping.
      */}
      <span className="tw-min-w-0">{children}</span>
      {!loading && end && <span className={`${iconSizeClasses[iconSize]} tw-flex tw-items-center tw-justify-center`}>{end}</span>}
      {/*
        The badge is a SIBLING of the children span, never inside it. `children` is
        wrapped in a `<span>` (inline), and `Badge`'s root is `display: flex`
        (block-level) — so a Badge passed in as a child lands inside that inline span,
        breaks onto its own line, and stretches to the span's full width. That shipped
        on the Compatibility page's Rule Action Queue control (§ 111): a full-width navy
        pill under the label. Use `badge`, which is what this slot is for; the type
        system cannot stop a Badge being passed as a child, so the docblock has to.
      */}
      {badgeValue !== undefined && (
        <Badge
          /* § 297 — the count follows the shape: a loading button keeps its fill, so it
             keeps the on-fill bubble; only an explicitly disabled one dims its count. */
          variant={badgeVariant ?? getBadgeVariant(variant, disabled)}
          data-testid={dataTestId ? `${dataTestId}-badge` : undefined}
        >
          {badgeValue}
        </Badge>
      )}
    </button>
  );
});

export default Button;
